// ═══════════════════════════════════════════
// EMH Order System — Direct Firebase Write
// ═══════════════════════════════════════════
//
// CRITICAL: commitOrder() bypasses SyncContext entirely.
// It writes directly to Firebase via update() with per-path keys.
// After write, SyncContext v4 listeners auto-refresh local state.
//
// This prevents the race condition where two devices each create
// an order and then overwrite each other's full orders array.

import { ref, get, update } from "firebase/database";
import { database } from "../firebase/config";

/**
 * Custom error thrown when stock is insufficient for order items.
 * Contains a `shortages` array with details per item.
 */
export class StockShortageError extends Error {
  constructor(shortages) {
    super('Stock shortage');
    this.name = 'StockShortageError';
    this.shortages = shortages; // [{ name, sku, location, requested, available }]
  }
}

/**
 * Local date string (YYYY-MM-DD) using device timezone (JST in production).
 * toISOString() returns UTC, which shifts dates before 09:00 JST to the previous day.
 */
function toLocalDateStr(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Generate a collision-safe order ID.
 * Format: SLS-YYMMDD-XXXX (XXXX = random base36)
 */
function generateSlipNo(date = new Date()) {
  const dateStr = toLocalDateStr(date).replace(/-/g, "").slice(2);
  const uniqueSuffix = Date.now().toString(36).slice(-4).toUpperCase();
  return `SLS-${dateStr}-${uniqueSuffix}`;
}

/**
 * Find the next numeric key for an array-like Firebase path.
 * Firebase stores arrays as { "0": ..., "1": ..., "4": ... }
 * We need to find max key + 1 to append without overwriting.
 */
async function getNextArrayIndex(path) {
  const snap = await get(ref(database, path));
  if (!snap.exists()) return 0;
  const data = snap.val();
  const keys = Object.keys(data).map(Number).filter((n) => !isNaN(n));
  return keys.length > 0 ? Math.max(...keys) + 1 : 0;
}

/**
 * Parse SKU from a cart item's groupKey id.
 * Cart item id format: "BJ|BJ-FR-CB-URS-19|A1" or "MARUCO|VM-IT-TR-LB-20|LA4"
 * Returns { sku, location }
 */
function parseCartItemId(itemId) {
  if (!itemId) return { sku: '', location: '' };
  const parts = itemId.split('|');
  if (parts.length === 3) {
    return { sku: parts[1], location: parts[2] };
  }
  // Fallback: item might have sku/location fields directly
  return { sku: itemId, location: '' };
}

/**
 * FIFO bottle selection for stock deduction.
 * Given all bottles from Firebase, find in_stock bottles matching SKU+location,
 * sorted by created_at ASC (oldest first), and return the first N bottle IDs.
 *
 * @param {Object} allBottles - Firebase /bottle data (keyed by bottleId)
 * @param {string} sku - SKU to match
 * @param {string} location - shelf location to match
 * @param {number} qty - number of bottles needed
 * @returns {{ selected: string[], shortage: number }}
 */
function selectBottlesFIFO(allBottles, sku, location, qty) {
  // Find all in_stock bottles matching SKU + location
  const candidates = [];
  Object.entries(allBottles).forEach(([bottleId, b]) => {
    if (!b || b.status !== 'in_stock') return;
    if (b.sku !== sku) return;
    if (location && b.location !== location) return;
    candidates.push({
      id: bottleId,
      created_at: b.created_at || b.intake_date || '1970-01-01',
    });
  });

  // Sort FIFO: oldest first
  candidates.sort((a, b) => a.created_at.localeCompare(b.created_at));

  const selected = candidates.slice(0, qty).map(c => c.id);
  const shortage = Math.max(0, qty - selected.length);

  return { selected, shortage };
}

/**
 * Commit an order directly to Firebase.
 * Writes: /orders/{index}, /log/{index..index+N}, /bottle/{id} status updates
 * Does NOT touch SyncContext — listeners will auto-refresh local state.
 *
 * FIFO stock deduction: for each cart item, selects the oldest in_stock bottles
 * matching SKU+location and marks them as "sold" in the same atomic update.
 *
 * @param {Object} params
 * @param {Object} params.saleData - from POSModal onComplete
 * @param {Object} params.user - current user { id, name, role }
 * @param {Function} params.updateStock - callback to update local stock state
 * @returns {Object} { slipNo, order, soldBottles }
 */
export async function commitOrder({ saleData, user, updateStock }) {
  const now = new Date();
  const slipNo = generateSlipNo(now);
  const dateStr = toLocalDateStr(now);
  const isoNow = now.toISOString();

  // Build order object
  const newOrder = {
    id: slipNo,
    date: dateStr,
    items: saleData.items.map((item) => ({
      id: item.id,
      name: item.name,
      qty: item.qty,
      price: item.price,
      brand: item.brand,
      ...(item.itemDiscount ? { itemDiscount: item.itemDiscount } : {}),
    })),
    subtotal: saleData.subtotal,
    tax: saleData.tax,
    total: saleData.total,
    taxType: saleData.taxType,
    taxRate: saleData.taxRate || 10,
    paymentMethod: saleData.paymentMethod,
    customer: saleData.customerNote || "店頭顧客",
    customerId: saleData.customerId || null,
    staff: user.name,
    staffId: user.id,
    channel: "店頭販売",
    brand:
      saleData.items.every((i) => i.brand === "BJ")
        ? "BJ"
        : saleData.items.every((i) => i.brand === "MARUCO")
          ? "MARUCO"
          : "MIXED",
    discountAmount: saleData.discountAmount || 0,
    discountType: saleData.discountType || 'none',
    discountValue: saleData.discountValue || '',
    ...(saleData.shipping ? { shipping: saleData.shipping } : {}),
    ...(saleData.splitDetails ? { splitDetails: saleData.splitDetails } : {}),
    status: "completed",
    createdAt: isoNow,
  };

  // Build log entries
  const newLogs = saleData.items.map((item, idx) => {
    const lineTotal = (item.price || 0) * item.qty;
    const itemDiscAmt = item.itemDiscount
      ? item.itemDiscount.type === 'percent'
        ? Math.floor(lineTotal * (parseFloat(item.itemDiscount.value) || 0) / 100)
        : (parseInt(item.itemDiscount.value) || 0)
      : 0;
    return {
    id: `${Date.now()}-${idx}`,
    date: dateStr,
    slipNo,
    productId: item.id,
    productName: item.name,
    type: "出庫",
    qty: item.qty,
    cost: item.cost || 0,
    price: item.price || 0,
    total: lineTotal - itemDiscAmt,
    channel: "店頭販売",
    brand:
      item.brand ||
      (item.id?.startsWith("BJ|") ? "BJ" : item.id?.startsWith("MARUCO|") ? "MARUCO" : "BJ"),
    taxType: saleData.taxType,
    paymentMethod: saleData.paymentMethod,
    customer: saleData.customerNote || "店頭顧客",
    customerId: saleData.customerId || null,
    staff: user.name,
    orderSubtotal: saleData.subtotal,
    orderTax: saleData.tax,
    orderTotal: saleData.total,
    taxRate: saleData.taxRate || 10,
  };
  });

  // Get next indices + all bottles in parallel
  const [orderIdx, logIdx, bottleSnap] = await Promise.all([
    getNextArrayIndex("orders"),
    getNextArrayIndex("log"),
    get(ref(database, "bottle")),
  ]);

  const allBottles = bottleSnap.exists() ? bottleSnap.val() : {};

  // Build Firebase update payload
  const updates = {};
  updates[`orders/${orderIdx}`] = newOrder;
  newLogs.forEach((log, i) => {
    updates[`log/${logIdx + i}`] = log;
  });

  // ── FIFO stock deduction ──
  // For each cart item, find matching in_stock bottles and mark as sold.
  // Uses a working copy of allBottles to avoid selecting the same bottle twice
  // when multiple cart items share the same SKU.
  const workingBottles = { ...allBottles };
  const soldBottles = []; // Track for return value
  const shortages = []; // Collect shortages to throw at end

  for (const item of saleData.items) {
    const { sku, location } = parseCartItemId(item.id);
    if (!sku) continue;

    // Also try item.sku / item.location as fallback
    const effectiveSku = sku || item.sku || '';
    const effectiveLocation = location || item.location || '';

    const { selected, shortage } = selectBottlesFIFO(
      workingBottles, effectiveSku, effectiveLocation, item.qty
    );

    if (shortage > 0) {
      shortages.push({
        name: item.name || effectiveSku,
        sku: effectiveSku,
        location: effectiveLocation,
        requested: item.qty,
        available: item.qty - shortage,
      });
    }

    // Mark selected bottles as sold in the update payload
    selected.forEach(bottleId => {
      updates[`bottle/${bottleId}/status`] = 'sold';
      updates[`bottle/${bottleId}/sold_at`] = isoNow;
      updates[`bottle/${bottleId}/order_id`] = slipNo;

      // Remove from working copy so next item won't re-select same bottle
      delete workingBottles[bottleId];

      soldBottles.push({ bottleId, sku: effectiveSku, location: effectiveLocation });
    });
  }

  // Throw if any shortages detected — caller (POSModal) catches and shows UI
  if (shortages.length > 0) {
    throw new StockShortageError(shortages);
  }

  // Update customer stats if linked
  if (saleData.customerId) {
    const custSnap = await get(ref(database, "customer"));
    if (custSnap.exists()) {
      const custData = custSnap.val();
      // Find customer by id field (customer is stored as array-like object)
      const custEntry = Object.entries(custData).find(
        ([, c]) => c && c.id === saleData.customerId
      );
      if (custEntry) {
        const [custKey, custVal] = custEntry;
        updates[`customer/${custKey}/totalOrders`] = (custVal.totalOrders || 0) + 1;
        updates[`customer/${custKey}/totalSpent`] = (custVal.totalSpent || 0) + (newOrder.total || 0);
        updates[`customer/${custKey}/lastOrder`] = dateStr;
        // Bug 1 fix: auto-promote trial → active on first confirmed sale
        if ((custVal.tier || '').toLowerCase() === 'trial') {
          updates[`customer/${custKey}/tier`] = 'active';
        }
      }
    }
  }

  // Write to Firebase atomically (order + logs + bottle status updates in one call)
  await update(ref(database, "/"), updates);

  // Update stock locally (immediate UI feedback)
  // SyncContext v4 listeners will also auto-refresh from Firebase,
  // but this provides instant visual feedback.
  if (updateStock) {
    updateStock(saleData.items);
  }

  console.log(
    `[commitOrder] ${slipNo}: ${soldBottles.length} bottles marked sold`,
    soldBottles.map(b => b.bottleId)
  );

  return { slipNo, order: newOrder, soldBottles };
}
